import { datadogRum } from '@datadog/browser-rum'
import { useRouter } from 'next/router'
import {
  createContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { getInsuranceProviderRoute } from 'dataSource/insuranceProvider/routes'
import useApi from 'hooks/useApi'
import { getParsedQuery, hasError, modifyURLQuery } from 'utils/common'

const insuranceProviderInitial = {
  insuranceProvider: null,
  insuranceDisplayValue: '',
  setInsuranceData: (_insuranceProvider: InsuranceProvider | null) => null,
  selfPay: false,
  // True when there is no in-flight URL insuranceId lookup (or none was needed).
  isInsuranceProviderResolved: true,
}

export const InsuranceProviderContext = createContext(insuranceProviderInitial)

const getRequestedInsuranceId = (insuranceId?: string | string[]) => {
  if (!insuranceId) {
    return null
  }

  return String(insuranceId)
}

// This context provider component handles the state for insurance
// Listens to the url router to check insuranceId and gets insurance accordingly
// This component should be wrapped on page level and not on component level
const InsuranceProvider: React.FC = ({ children }) => {
  //---------------------------- states
  const router = useRouter()
  const { request } = useApi()
  // asPath is available before Next hydrates router.query; keep URL reads aligned
  // with SelectInsurancePage (getParsedQuery) so we do not look "resolved" too early.
  const parsedQuery = getParsedQuery(router)
  const insuranceId = router.query.insuranceId ?? parsedQuery.insuranceId
  const selfPayParam = router.query.selfPay ?? parsedQuery.selfPay
  const isSelfPayUrl = selfPayParam === 'true'

  const [insuranceProvider, setInsuranceProvider] = useState<InsuranceProvider>(
    null
  )
  const [insuranceDisplayValue, setInsuranceDisplayValue] = useState<string>('')
  const [selfPay, setSelfPay] = useState<boolean>(false)
  // Last URL request key whose lookup finished (success or failure). Exposed
  // resolution is derived from this so a new key is pending until completed.
  const [resolvedRequestKey, setResolvedRequestKey] = useState<string | null>(
    null
  )
  const activeInsuranceIdRef = useRef<string | null>(null)
  const currentRequestKeyRef = useRef<string | null>(null)

  const needsProviderLookup = !isSelfPayUrl && Boolean(insuranceId)
  const currentRequestKey = needsProviderLookup
    ? getRequestedInsuranceId(insuranceId)
    : null
  currentRequestKeyRef.current = currentRequestKey

  const isInsuranceProviderResolved =
    !needsProviderLookup || resolvedRequestKey === currentRequestKey

  // Invalidate in-flight work as soon as the URL key changes, before passive
  // effects run and before a deferred response for the previous key can apply.
  useLayoutEffect(() => {
    if (isSelfPayUrl || !insuranceId) {
      activeInsuranceIdRef.current = null
      return
    }

    activeInsuranceIdRef.current = getRequestedInsuranceId(insuranceId)
  }, [insuranceId, isSelfPayUrl])

  const canApplyRequestResult = (requestedInsuranceId: string | null) =>
    activeInsuranceIdRef.current === requestedInsuranceId &&
    currentRequestKeyRef.current === requestedInsuranceId

  //----------------------------- getInsuranceAPI
  const getInsuranceProviderAPI = async ({
    insuranceId,
  }: {
    insuranceId: string
  }) => {
    const requestedInsuranceId = getRequestedInsuranceId(insuranceId)

    activeInsuranceIdRef.current = requestedInsuranceId
    // Pending until this key completes; keeps derived resolution false for the
    // active URL key (including same-key refetch).
    setResolvedRequestKey(null)

    try {
      const response = await request.get(getInsuranceProviderRoute(insuranceId))

      // useFetch resolves on 4xx instead of throwing, so an insuranceId that
      // does not exist in this environment would otherwise be stored as the
      // error body and never reach the cleanup path below.
      hasError(request, response)

      const insuranceProvider = response as InsuranceProvider

      if (!canApplyRequestResult(requestedInsuranceId)) {
        return
      }

      setInsuranceData(insuranceProvider)
      setResolvedRequestKey(requestedInsuranceId)
    } catch (error) {
      if (!canApplyRequestResult(requestedInsuranceId)) {
        return
      }

      datadogRum.addError(error)
      activeInsuranceIdRef.current = null
      setInsuranceProvider(null)
      setInsuranceDisplayValue('')
      setResolvedRequestKey(requestedInsuranceId)
      modifyURLQuery(router, { insuranceId: null }, false, 'replace')
    }
  }

  const setInsuranceData = (insuranceProvider: InsuranceProvider | null) => {
    const insuranceDisplayValue = insuranceProvider?.name || ''

    setInsuranceProvider(insuranceProvider)
    setInsuranceDisplayValue(insuranceDisplayValue)
  }

  useEffect(() => {
    if (isSelfPayUrl) {
      activeInsuranceIdRef.current = null
      setSelfPay(true)
      setInsuranceDisplayValue('Self Pay')
      setInsuranceProvider(null)
      setResolvedRequestKey(null)
      return
    }

    setSelfPay(false)

    if (insuranceId) {
      getInsuranceProviderAPI({ insuranceId: String(insuranceId) })
    } else {
      activeInsuranceIdRef.current = null
      setInsuranceProvider(null)
      setInsuranceDisplayValue('')
      setResolvedRequestKey(null)
    }
  }, [insuranceId, isSelfPayUrl])

  // Hide stale provider while a new URL key is pending, and while the URL
  // already indicates self-pay (state may lag one effect until cleared).
  const shouldExposeLookupResult =
    !isSelfPayUrl && (!needsProviderLookup || isInsuranceProviderResolved)

  return (
    <InsuranceProviderContext.Provider
      value={{
        insuranceProvider: shouldExposeLookupResult ? insuranceProvider : null,
        insuranceDisplayValue: isSelfPayUrl
          ? 'Self Pay'
          : shouldExposeLookupResult
          ? insuranceDisplayValue
          : '',
        setInsuranceData,
        selfPay: isSelfPayUrl || selfPay,
        isInsuranceProviderResolved,
      }}
    >
      {children}
    </InsuranceProviderContext.Provider>
  )
}

export default InsuranceProvider
